import { inject, Injectable } from '@angular/core';
import {
  ConsultantEtiquettesGQL,
  EtiquetteDto,
  EtiquetteDtoInput,
  EtiquetteFragmentFragment,
  EtiquetteInput,
  UpdateEtiquettesGQL,
} from '@lib/models/generated/graphql';
import { QueryManagerService } from 'finzzle-angular-utils';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { OuiEtiquette } from 'omnium-ui/select-etiquette';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class EtiquetteService {
  private queryManagerService = inject(QueryManagerService);
  private getConsultantEtiquettesGQL = inject(ConsultantEtiquettesGQL);
  private updateEtiquettesGQL = inject(UpdateEtiquettesGQL);
  public etiquettesSubject = new BehaviorSubject<EtiquetteFragmentFragment[]>([]);
  public etiquettes$ = this.etiquettesSubject.asObservable();
  constructor() {
    this.getConsultantEtiquettes(null);
  }
  public etiquettesAsSelectOptions(): Observable<OuiSelectOption[]> {
    return this.etiquettes$.pipe(
      map(etiquettes =>
        etiquettes.map(etiquette => ({
          label: etiquette.label,
          value: etiquette.id,
        }))
      ),
      startWith([])
    );
  }
  public async getConsultantEtiquettes(codeConsultant: string | null): Promise<void> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getConsultantEtiquettesGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { consultantCode: codeConsultant },
      })
    );
    if (result) {
      this.etiquettesSubject.next(result.data!.consultantEtiquettes);
    }
  }
  public async updateEtiquettes(
    investisseurIds: number[],
    etiquettes: OuiEtiquette[],
    selectedEtiquettesMap: Map<number, number[]>
  ): Promise<EtiquetteDto[] | undefined> {
    const etiquetteInput = this.mapEtiquettesToEtiquetteInput(investisseurIds, etiquettes, selectedEtiquettesMap);
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.updateEtiquettesGQL.mutate({
        variables: { etiquetteInput },
      })
    );
    this.etiquettesSubject.next(
      (result.data!.updateEtiquettes ?? []).map(e => {
        return { id: e.id, color: e.color, label: e.label };
      })
    );
    return result.data!.updateEtiquettes;
  }
  private mapEtiquettesToEtiquetteInput(
    investisseurIds: number[],
    etiquettes: OuiEtiquette[],
    selectedEtiquettesMap: Map<number, number[]>
  ): EtiquetteInput {
    return {
      investisseurIds,
      etiquettes: etiquettes
        .filter(e => e.label.trim() !== '')
        .map(e => {
          const etiquetteDto = {} as EtiquetteDtoInput;
          etiquetteDto.id = e.id;
          etiquetteDto.color = e.color;
          etiquetteDto.label = e.label.trim();
          etiquetteDto.selectedInvestisseurIds = e.id === 0 ? investisseurIds : (selectedEtiquettesMap.get(e.id) ?? []);
          return etiquetteDto;
        }),
    };
  }
}
